import * as React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getTranslate } from 'react-localize-redux';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { bindActionCreators } from 'redux';
import classnames from 'classnames';
import {
    withStyles, Typography, Accordion, AccordionSummary, AccordionDetails,
} from '@material-ui/core';

import {
    ContentBox, TopActionBar, BottomActionBar, VimoModal,
} from '../../common/components';
import { getTrainingModules } from '../../common/utils/programUtils';
import { Loading } from '../../youwell-common/components';
import { toLookup } from '../../youwell-common/arrayUtils';
import { ExpandIcon } from '../../common/icons';
import urls from '../../app/urls';

import * as PatientStore from '../../app/store/PatientStore';

import DragableSkill from './components/DragableSkill';
import SelectedSkill from './components/SelectedSkill';
import SkillInfo from './components/SkillInfo';
import { vimoElementGroups } from '../../common/constants';
import { today } from '../../youwell-common/dateUtils';

const SELECTED_SKILLS_DROP_ID = 'SELECTED_SKILLS_DROP_ID';
const ALL_SKILLS_DROP_ID = 'ALL_SKILLS_DROP_ID';
const taskWidth = 120;

const styles = theme => ({
    root: {
    },
    content: {
        padding: theme.spacing(4),
    },
    skillsSelection: {
        display: 'flex',
        justifyContent: 'space-between',
        margin: `${theme.spacing(4)}px ${-theme.spacing(1)}px`,
    },
    selectedSkills: {
    },
    selectedSkillsContent: {
        border: theme.borders.dashed,
        width: (taskWidth * 2) + theme.spacing(4) + 4,
        minHeight: 200,
        display: 'flex',
        flexWrap: 'wrap',
        position: 'relative',
    },
    allSkills: {
    },
    allSkillsContent: {
        width: (taskWidth * 3) + theme.spacing(6) + 4,
    },
    expansionPanelExpanded: {
        border: theme.borders.dashed,
    },
    allSkillsContentModules: {
        display: 'flex',
        flexWrap: 'wrap',
        padding: 0,
    },
    skillsTask: {
        width: taskWidth,
        margin: theme.spacing(1),
    },
    selectedSkillPlaceholder: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        opacity: 0.1,
    },
    isDraggingOver: {
        background: theme.colors.green1,
    },
    description: {
        margin: theme.spacing(2),
    },

});

const initialState = {
    studentId: '',
    updated: '',
    selectedModules: [],
    hasUnsavedChanges: false,
    saveMessage: '',
};

const getStateFromData = (studentData) => {
    const deafultState = {
        ...initialState,
        studentId: studentData && studentData.id,
        updated: studentData && studentData.updated,
    };

    if (!studentData || !studentData.programData) {
        return deafultState;
    }

    return {
        ...deafultState,
        completeDetailsLoaded: studentData.programData && !studentData.programData.limitedDetails,
        visibleCategory: false,
        selectedModules: getTrainingModules(studentData.programData) || [],
    };
};

class Skills extends React.Component {
    state = getStateFromData(this.props.studentData)

    static getDerivedStateFromProps(props, state) {
        if (props.studentData
            && (props.studentData.id !== state.studentId
                || props.studentData.updated !== state.updated
                || (props.studentData.programData && !state.completeDetailsLoaded && !props.studentData.programData.limitedDetails))) {
            return getStateFromData(props.studentData);
        }
        return null;
    }

    toggleCategory = category => (event, expanded) => {
        this.setState({
            visibleCategory: expanded ? category : false,
        });
    }

    showModuleInfo = module => () => {
        this.setState({ showModuleInfo: module });
    }

    hideModuleInfo = () => {
        this.setState({ showModuleInfo: null });
    }

    removeSkill = moduleId => () => {
        this.setState(prevState => ({
            selectedModules: prevState.selectedModules.filter(d => d.id !== moduleId),
            hasUnsavedChanges: true,
            saveMessage: '',
        }));
    }

    addSkill = moduleIndex => () => {
        this.setState(prevState => ({
            selectedModules: [...prevState.selectedModules, this.props.moduleList[moduleIndex]],
            hasUnsavedChanges: true,
            saveMessage: '',
        }));
    }

    onDragEnd = (result) => {
        const { source, destination, draggableId } = result;

        if (!destination) {
            if (source.droppableId === SELECTED_SKILLS_DROP_ID) {
                this.removeSkill(draggableId)();
            }
        } else if (destination.droppableId === SELECTED_SKILLS_DROP_ID) {
            if (source.droppableId === ALL_SKILLS_DROP_ID) {
                this.addSkill(source.index)();
            }
        }
    }

    save = () => {
        const programData = this.props.studentData.programData || {
            patientId: this.props.studentData.id,
            programId: this.props.vimoProgramId,
            startDate: today(),
            elements: [],
        };
        const existingElements = programData.elements || [];
        const otherElements = existingElements.filter(d => d.elementGroup !== vimoElementGroups.main);

        const selectedModulesLookup = toLookup(this.state.selectedModules);
        const existingModulesLookup = existingElements
            .reduce((prev, curr) => (
                curr.module ? { ...prev, [curr.module.id]: true } : prev
            ), {});

        const newElements = this.state.selectedModules.filter(d => !existingModulesLookup[d.id]).map(module => (
            { module: { id: module.id }, sorting: module.sorting, elementGroup: vimoElementGroups.main }
        ));

        const patientProgram = {
            ...programData,
            elements: [
                ...otherElements,
                ...existingElements
                    .filter(d => d.module && selectedModulesLookup[d.module.id]),
                ...newElements,
            ],
        };

        if (programData.id) {
            this.props.updateProgramForPatient(patientProgram, {
                callback: () => {
                    this.setState({
                        hasUnsavedChanges: false,
                        saveMessage: this.props.translate('studentPlan.allChangesSaved'),
                    });
                },
                redirect: `${urls.inside.student.home}/${this.props.studentData.id}/${urls.inside.student.parts.plan}`,
                reducerParams: { patientId: this.props.studentData.id },
            });
        } else {
            this.props.insertProgramForPatient(patientProgram, {
                callback: () => {
                    this.setState({
                        hasUnsavedChanges: false,
                        saveMessage: this.props.translate('studentPlan.allChangesSaved'),
                    });
                },
                redirect: `${urls.inside.student.home}/${this.props.studentData.id}/${urls.inside.student.parts.plan}`,
                reducerParams: { patientId: this.props.studentData.id },
            });
        }
    }

    render() {
        const {
            classes, translate, studentData, moduleList, profileData,
        } = this.props;

        if (!studentData) {
            return null;
        }

        const selectedLookup = toLookup(this.state.selectedModules);

        const moduleCategories = [
            { id: 'communication', modules: [] },
            { id: 'play', modules: [] },
            { id: 'friendship', modules: [] },
            { id: 'selfcontrol', modules: [] },
            { id: 'conflicts', modules: [] },
        ];

        if (moduleList) {
            for (let i = 0; i < moduleList.length; i++) {
                const el = { ...moduleList[i], index: i };
                if (i < 23) {
                    moduleCategories[0].modules.push(el);
                } else if (i < 32) {
                    moduleCategories[1].modules.push(el);
                } else if (i < 47) {
                    moduleCategories[2].modules.push(el);
                } else if (i < 60) {
                    moduleCategories[3].modules.push(el);
                } else {
                    moduleCategories[4].modules.push(el);
                }
            }
        }

        const canEdit = profileData?.userAccess?.editPatients;

        return (
            <div className={classes.root}>
                <TopActionBar
                    title={translate('studentSkills.selectSkills')}
                    onSave={this.save}
                    saveEnabled={this.state.hasUnsavedChanges}
                    backLink={{
                        to: `${urls.inside.student.home}/${studentData.id}/${urls.inside.student.parts.plan}`,
                        text: translate('studentSkills.backToPlan'),
                    }}
                />

                {this.state.showModuleInfo && (
                    <VimoModal
                        open={!!this.state.showModuleInfo}
                        onClose={this.hideModuleInfo}
                        heading={this.state.showModuleInfo.name}
                    >
                        <SkillInfo skillId={this.state.showModuleInfo.id} hideLink />
                    </VimoModal>
                )}

                <div className={classes.content}>
                    <Typography variant="body2" className={classes.description} color="primary">
                        {translate('studentSkills.selectSkillsDescription')}
                    </Typography>
                    <div className={classes.skillsSelection}>
                        <DragDropContext onDragEnd={this.onDragEnd}>
                            <ContentBox
                                className={classes.selectedSkills}
                                heading={translate('studentSkills.selectedSkills')}
                            >
                                <div className={classes.selectedSkillsContent}>
                                    <Droppable droppableId={SELECTED_SKILLS_DROP_ID} direction="horizontal">
                                        {(provided, snapshot) => (
                                            <div
                                                className={classnames(classes.selectedSkillPlaceholder, { [classes.isDraggingOver]: snapshot.isDraggingOver })}
                                                ref={provided.innerRef}
                                                {...provided.droppableProps}
                                            >
                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>
                                    {this.state.selectedModules.map(module => (
                                        <SelectedSkill
                                            key={module.id}
                                            module={module}
                                            showInfo={this.showModuleInfo(module)}
                                            className={classes.skillsTask}
                                            onRemove={this.removeSkill(module.id)}
                                            disabled={!canEdit}
                                        />
                                    ))}
                                </div>
                            </ContentBox>
                            <ContentBox
                                className={classes.allSkills}
                                heading={translate('studentSkills.skillsLibrary')}
                            >
                                <Droppable droppableId={ALL_SKILLS_DROP_ID} isDropDisabled>
                                    {provided => (
                                        <div
                                            className={classes.allSkillsContent}
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                        >
                                            {moduleList ? moduleCategories.map(category => (
                                                <Accordion
                                                    key={category.id}
                                                    expanded={this.state.visibleCategory === category.id}
                                                    onChange={this.toggleCategory(category.id)}
                                                    classes={{ expanded: classes.expansionPanelExpanded }}
                                                >
                                                    <AccordionSummary expandIcon={<ExpandIcon />}>
                                                        <Typography>{translate(`moduleCategories.${category.id}`)}</Typography>
                                                    </AccordionSummary>
                                                    <AccordionDetails className={classes.allSkillsContentModules}>
                                                        {category.modules.map(module => (
                                                            <DragableSkill
                                                                key={module.id}
                                                                module={module}
                                                                showInfo={this.showModuleInfo(module)}
                                                                index={module.index}
                                                                draggableId={module.id}
                                                                disabled={!!selectedLookup[module.id] || !canEdit}
                                                                className={classes.skillsTask}
                                                                onAdd={selectedLookup[module.id] ? null : this.addSkill(module.index)}
                                                            />
                                                        ))}
                                                    </AccordionDetails>
                                                </Accordion>
                                            )) : (
                                                <Loading loading inline />
                                            )}
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            </ContentBox>
                        </DragDropContext>
                    </div>
                </div>

                {canEdit && (
                    <BottomActionBar
                        cancelLink={`${urls.inside.student.home}/${studentData.id}/${urls.inside.student.parts.plan}`}
                        onSave={this.save}
                        saveEnabled={this.state.hasUnsavedChanges}
                        saveMessage={this.state.saveMessage}
                    />
                )}
            </div>
        );
    }
}

Skills.propTypes = {
    classes: PropTypes.object.isRequired,
    translate: PropTypes.func.isRequired,
    vimoProgramId: PropTypes.string.isRequired,
    updateProgramForPatient: PropTypes.func.isRequired,
    insertProgramForPatient: PropTypes.func.isRequired,
    studentData: PropTypes.object,
    moduleList: PropTypes.array,
    profileData: PropTypes.object,
};

Skills.defaultProps = {
    studentData: null,
    moduleList: null,
    profileData: null,
};

const mapStateToProps = (state, ownProps) => ({
    translate: getTranslate(state.localize),
    profileData: state.auth.profileData,
    studentData: state.patient.details && state.patient.details[ownProps.match.params.studentId],
    moduleList: state.program.programDetails && state.program.programDetails.modules,
    vimoProgramId: state.pageSettings.appConfig.vimoProgramId,
});

const mapDispatchToProps = dispatch => ({
    ...bindActionCreators(PatientStore.actionCreators, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(Skills));
